import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * The last line of defence between one bad record and an app nobody can use.
 *
 * Content in this app is stored as whole documents and synced between devices,
 * so a record that throws during render is not a local annoyance: React unmounts
 * the tree, the page goes white, reloading restores the same record from
 * localStorage, and signing in on a second device pulls it there too. There is
 * no screen left to delete it from.
 *
 * That risk grew when islands stopped being only hand-written. Generated
 * content is validated before it is saved, but validation is a claim about the
 * shapes we thought of. This catches the ones we did not, and — more to the
 * point — leaves a way out: the export button still works, because it reads
 * localStorage directly rather than through the component that just died.
 */
interface Props {
  children: ReactNode
  /** Offered as the way back; wired to the same reset Settings uses. */
  onReset?: () => void
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // No telemetry here by design — this app keeps a learner's content on their
    // own device. The console is where a person debugging their own install
    // will look.
    console.error('Something in your content could not be rendered.', error, info.componentStack)
  }

  private downloadBackup() {
    try {
      const raw = localStorage.getItem('language-island:v1') ?? '{}'
      const url = URL.createObjectURL(new Blob([raw], { type: 'application/json' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `language-island-backup-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      window.alert('Could not read the local copy of your content.')
    }
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="card" style={{ margin: '2rem auto', maxWidth: '38rem' }}>
        <h1 style={{ marginTop: 0 }}>Something in your content broke this screen</h1>
        <p className="muted">
          One of your islands or scenarios is malformed, and the app could not draw it. Your content
          is still on this device — take a copy before you do anything else.
        </p>

        <div className="row" style={{ marginTop: '1rem' }}>
          <button type="button" className="btn is-primary" onClick={() => this.downloadBackup()}>
            Download a backup
          </button>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
          {this.props.onReset && (
            <button
              type="button"
              className="btn is-danger"
              onClick={() => {
                if (window.confirm('Clear the content on this device and start from the starter set?')) {
                  this.props.onReset?.()
                }
              }}
            >
              Reset this device
            </button>
          )}
        </div>

        <div className="divider" />
        <div className="stat-label">what went wrong</div>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', overflowX: 'auto' }}>
          {this.state.error.message}
        </pre>
      </div>
    )
  }
}
